import { PiggyBank, TrendingDown, TrendingUp, Check } from 'lucide-react';
import { useDebtProvisionState } from '../hooks/useDebts.js';
import { formatCurrency } from '../lib/format.js';

const SCOPE_LABELS = {
  principal:              'Nominal seul',
  principal_and_interest: 'Nominal + intérêts',
};

/**
 * Suivi de la provision d'une dette : théorique vs solde réel du compte dédié,
 * avec l'écart chiffré.
 *
 * Ce bloc remplace LinkedProvisionBlock quand provision_scope != 'none' :
 * l'ancien module `provisions` est virtuel (il compare une dotation théorique à
 * la consommation par catégorie et ne lit jamais le solde bancaire). Afficher
 * les deux donnerait deux « montant provisionné » contradictoires pour la même
 * dette.
 */
export default function DebtProvisionTracker({ debt }) {
  const { data: state, isLoading } = useDebtProvisionState(debt.id);

  if (isLoading || !state?.enabled) return null;

  const {
    total_to_provision_cents,
    monthly_provision_cents,
    expected_balance_cents,
    funding_gap_cents,
    attributed_balance_cents,
    provision_account,
    share_pct,
  } = state;

  const shared = (provision_account?.debt_count ?? 0) > 1;

  // Tolérance à 1 € : sous ce seuil c'est de l'arrondi, pas une action à mener.
  const gapKnown = funding_gap_cents != null;
  const onTrack  = gapKnown && Math.abs(funding_gap_cents) < 100;
  const short    = gapKnown && funding_gap_cents <= -100;

  const gapTone = !gapKnown ? 'text-slate-400'
    : onTrack ? 'text-emerald-600'
    : short   ? 'text-red-600'
    : 'text-sky-600';

  return (
    <div className="bg-white rounded-xl border border-emerald-200 shadow-sm px-4 py-4 space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <PiggyBank size={16} className="text-emerald-600 shrink-0" aria-hidden />
          <h2 className="text-sm font-semibold text-slate-800">Provision dédiée</h2>
        </div>
        <span className="text-[11px] text-slate-500">
          {SCOPE_LABELS[state.provision_scope] ?? state.provision_scope}
        </span>
      </div>

      {/* L'écart — la seule ligne qui appelle une action */}
      <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-3">
        {!gapKnown ? (
          <p className="text-sm text-slate-500">
            Solde du compte dédié indisponible — impossible de calculer l'écart.
          </p>
        ) : onTrack ? (
          <p className={`text-sm font-medium flex items-center gap-1.5 ${gapTone}`}>
            <Check size={15} aria-hidden />
            Provision à jour
          </p>
        ) : (
          <>
            <p className={`text-sm font-medium flex items-center gap-1.5 ${gapTone}`}>
              {short
                ? <><TrendingDown size={15} aria-hidden /> Il manque {formatCurrency(Math.abs(funding_gap_cents))}</>
                : <><TrendingUp size={15} aria-hidden /> Avance de {formatCurrency(funding_gap_cents)}</>}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {short
                ? 'À virer sur le compte dédié pour rattraper le plan de provisionnement.'
                : 'Le compte dédié est alimenté au-delà du plan de provisionnement.'}
            </p>
          </>
        )}
      </div>

      {/* Théorique vs réel */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-xs text-slate-400">Provision attendue à date</p>
          <p className="text-lg font-bold tabular-nums text-slate-900">
            {formatCurrency(expected_balance_cents)}
          </p>
        </div>
        <div>
          <p className="text-xs text-slate-400">
            {shared ? 'Quote-part du compte' : 'Solde du compte dédié'}
          </p>
          <p className="text-lg font-bold tabular-nums text-slate-900">
            {attributed_balance_cents != null ? formatCurrency(attributed_balance_cents) : '—'}
          </p>
          {shared && (
            <p className="text-[11px] text-slate-400">
              {share_pct} % de {formatCurrency(provision_account.balance_cents)}
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 border-t border-slate-100 pt-3">
        <div>
          <p className="text-xs text-slate-400">Dotation mensuelle</p>
          <p className="text-sm font-semibold tabular-nums text-slate-800">
            {formatCurrency(monthly_provision_cents)}
          </p>
        </div>
        <div>
          <p className="text-xs text-slate-400">Total à provisionner</p>
          <p className="text-sm font-semibold tabular-nums text-slate-800">
            {formatCurrency(total_to_provision_cents)}
          </p>
        </div>
      </div>

      {provision_account && (
        <p className="text-xs text-slate-500">
          Compte : {provision_account.name}
          {shared && (
            <> — partagé avec {provision_account.debt_count - 1} autre
              {provision_account.debt_count > 2 ? 's' : ''} dette
              {provision_account.debt_count > 2 ? 's' : ''}
            </>
          )}
        </p>
      )}

      {/* Les virements vers le compte dédié sont des transferts internes :
          net-neutres sur la trésorerie totale, donc absents du forecast. */}
      <p className="text-[11px] text-slate-400">
        Les virements de provision sont des transferts internes — ils n'apparaissent
        pas dans le cash forecast. Seules les échéances réelles en sortent.
      </p>
    </div>
  );
}
